import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Box, Flex, Text, Paper, ThemeIcon, Badge, Loader, Center, Button, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCircleCheck, IconBuilding, IconArrowLeft } from "@tabler/icons-react";
import { useUserContext } from "@/context/UserContext";
import trpc from "@/shared/utils/trpc";
import PaymentMethodModal from "./PaymentMethodModal";
import CollaborationPaymentSuccessModal from "./CollaborationPaymentSuccessModal";
import type { CollaborationPaymentSuccessData } from "../hooks/useCollaborationEngagementPayment";
import { getIndustryLabel, regionsData } from "@/components/shared/constants/general.const";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";

export default function MatchSuccessPage() {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeMatchId } = useParams<{ id: string }>();
  const { userDetails } = useUserContext();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentModalOpened, { open: openPaymentModal, close: closePaymentModal }] = useDisclosure(false);
  const [engagementFeeCredits, setEngagementFeeCredits] = useState(14);
  const [latestRequest, setLatestRequest] = useState<any>(location.state?.request ?? null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [successPaymentData, setSuccessPaymentData] = useState<CollaborationPaymentSuccessData | null>(null);

  useEffect(() => {
    trpc.paymentConfig.get.query().then((config) => {
      setEngagementFeeCredits(config.engagementFeeCredits);
    });
  }, []);

  useEffect(() => {
    if (!routeMatchId) return;
    let cancelled = false;

    const fetchLatestRequest = async () => {
      setRequestLoading(true);
      try {
        const collaboration = await trpc.collaboration.getById.query({ matchId: routeMatchId });
        if (!cancelled) {
          setLatestRequest(collaboration ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          handleTrpcError(error);
        }
      } finally {
        if (!cancelled) {
          setRequestLoading(false);
        }
      }
    };

    fetchLatestRequest();

    return () => {
      cancelled = true;
    };
  }, [routeMatchId]);

  // Get request data from navigation state
  const request = latestRequest ?? location.state?.request;
  if (!request || requestLoading) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const matchId = request.id;
  const userCompanyId = userDetails?.company?.id;
  const isRequester = userCompanyId === request.requesterCompanyId;
  const userPaid = isRequester ? Boolean(request.requesterPaid) : Boolean(request.receiverPaid);
  const yourCompanyId = userDetails?.company?.gtid || "CM-2025-XXXX";
  const otherCompany = isRequester ? request.receiver : request.requester;
  const matchedCompanyId = otherCompany?.gtid || "CM-2025-XXXX";

  const handleProceedToPayment = () => {
    if (userPaid) return;
    openPaymentModal();
  };

  const handlePaymentConfirm = async (payload?: { method?: "credits"; acceptNda?: boolean }) => {
    setPaymentLoading(true);
    const acceptNda = !!payload?.acceptNda;

    try {
      const result = await trpc.collaboration.payEngagementFee.mutate({ matchId, acceptNda });

      if (result.success) {
        closePaymentModal();

        const paymentMessage =
          result.newStatus === "BOTH_PAID"
            ? `${result.creditsCharged} credits charged from your wallet. Collaboration is active.`
            : result.creditsCharged > 0
              ? `${result.creditsCharged} credits charged. Waiting for the other party to pay.`
              : "No credits charged yet — both wallets are debited only after the other company pays too. Waiting for the other party.";

        successNotification({
          title: result.newStatus === "BOTH_PAID" ? "Payment Successful" : "Payment step recorded",
          message: paymentMessage,
        });

        setSuccessPaymentData({
          transactionId: `TXN-${new Date().getFullYear()}-${Date.now().toString().slice(-8)}`,
          creditsCharged: result.creditsCharged,
          date: new Date().toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
          paymentMethod: "Collaboration Credits",
          newStatus: result.newStatus,
          matchId,
          acceptedNdaWithPayment: acceptNda,
          bothNdaBecameSigned: !!(result as { bothNdaBecameSigned?: boolean }).bothNdaBecameSigned,
        });

        try {
          const collaboration = await trpc.collaboration.getById.query({ matchId });
          setLatestRequest(collaboration ?? null);
        } catch {
          /* keep existing request snapshot */
        }
      }
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <Box mih="100vh" pb={20}>
      <Box py="sm" visibleFrom="sm">
        <Flex justify="space-between" align="center">
          <Button
            variant="subtle"
            color="slate.4"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            onClick={() => navigate("/dashboard?tab=requests&filter=received")}
          >
            Back to Interest
          </Button>
        </Flex>
      </Box>
      {/* Header */}
      <Box style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Flex justify="space-between" align="center" px="xl" pb={{ base: "md", sm: "lg" }}>
          <Box>
            <Title order={2} fz={{ base: 20, sm: 24 }} fw={400} mb={4}>
              Match Success
            </Title>
            <Text size="sm" c="slate.5">
              Mutual interest confirmed - proceed to unlock company details
            </Text>
          </Box>
          <Button
            onClick={handleProceedToPayment}
            mt={{ base: 0, sm: "md" }}
            loading={paymentLoading}
            disabled={userPaid}
          >
            {userPaid ? "Payment Completed" : "Proceed to Payment"}
          </Button>
        </Flex>
      </Box> 

      {/* Main Content */}
      <Box px="xl">
        <Paper radius="lg" p="xl">
          {/* Success Banner */}
          <Paper
            radius="lg"
            p="xl"
            mb="lg"
            style={{
              background:
                "linear-gradient(to right, var(--mantine-color-primary-0), var(--mantine-color-primary-1) 50%, var(--mantine-color-primary-0))",
              border: "1px solid var(--mantine-color-primary-3)",
            }}
          >
            <Flex direction="column" align="center" gap="sm">
              <ThemeIcon size={64} radius={64} color="primary" variant="white">
                <IconCircleCheck size={36} />
              </ThemeIcon>
              <Text fz="lg" fw={400} ta="center">
                It's a Match!
              </Text>
              <Text c="slate.5" fz="sm" ta="center">
                Both companies have expressed mutual interest in collaborating
              </Text>
            </Flex>
          </Paper>

          {/* Collaboration Fee */}
          <Paper withBorder p="xl" radius="lg" mb="lg">
            <Flex justify="space-between" align="flex-start">
              <Box>
                <Text fw={400} mb={4}>
                  Collaboration Fee
                </Text>
                <Text fz="sm" c="slate.5" mb="md">
                  One-time payment for workspace access
                </Text>
                <Flex direction="column" gap="xs">
                  <Flex align="center" gap="xs">
                    <IconCircleCheck size={20} color="var(--mantine-color-slate-5)" />
                    <Text fz="sm" c="slate.5">
                      Full company details revealed
                    </Text>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <IconCircleCheck size={20} color="var(--mantine-color-slate-5)" />
                    <Text fz="sm" c="slate.5">
                      Auto-generated NDA & document sharing
                    </Text>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <IconCircleCheck size={20} color="var(--mantine-color-slate-5)" />
                    <Text fz="sm" c="slate.5">
                      Credits will be refunded if the other party does not pay within 96 hours
                    </Text>
                  </Flex>
                </Flex>
              </Box>
              <Box ta="right">
                <Text fz={28} fw={400} c="slate.8">
                  {engagementFeeCredits} Credits
                </Text>
                <Text fz="sm" c="slate.5">
                  per company
                </Text>
              </Box>
            </Flex>
          </Paper>

          {/* Match Details */}
          <Paper withBorder p="xl" radius="lg" mb="lg">
            <Text fw={400} mb="md">
              Match Details
            </Text>
            <Flex direction={{ base: "column", sm: "row" }} justify="space-between">
              {/* Your Company */}
              <Box>
                <Flex align="center" gap="sm" mb="md">
                  <ThemeIcon size={40} radius="lg" color="primary" variant="light">
                    <IconBuilding size={20} />
                  </ThemeIcon>
                  <Box>
                    <Text fz="xs" c="slate.5">
                      Your Company
                    </Text>
                    <Text fz="sm" fw={500}>
                      {yourCompanyId}
                    </Text>
                  </Box>
                </Flex>

                {/* Your Company - Industry Category */}
                {Array.isArray(userDetails?.company?.industryCategory) && userDetails.company.industryCategory.length > 0 && (
                  <Box mb="xs">
                    <Text fz="xs" c="slate.5" mb={4}>
                      Type of Company
                    </Text>
                    <Text fz="sm">
                      {getIndustryLabel(userDetails.company.industryCategory[0])}
                      {userDetails.company.industryCategory.length > 1 && (
                        <Text span c="slate.5"> +{userDetails.company.industryCategory.length - 1}</Text>
                      )}
                    </Text>
                  </Box>
                )}

                {/* Your Company - Preferred Regions */}
                {userDetails?.company?.preferredCollaborationRegions &&
                  userDetails.company.preferredCollaborationRegions.length > 0 && (
                    <Box>
                      <Text fz="xs" c="slate.5" mb={4}>
                        Preferred Collaboration Regions
                      </Text>
                      <Flex gap="xs" wrap="wrap">
                        {userDetails.company.preferredCollaborationRegions.map((region: string, index: number) => (
                          <Badge
                            key={index}
                            variant="light"
                            color="slate"
                            c="black"
                            py={10}
                            px={8}
                            size="md"
                            fw={400}
                            tt="none"
                          >
                            {getRegionLabel(region)}
                          </Badge>
                        ))}
                      </Flex>
                    </Box>
                  )}
              </Box>

              {/* Matched Company */}
              <Box>
                <Flex align="center" gap="sm" mb="md">
                  <ThemeIcon size={40} radius="lg" color="primary" variant="light">
                    <IconBuilding size={20} />
                  </ThemeIcon>
                  <Box>
                    <Text fz="xs" c="slate.5">
                      Matched Company
                    </Text>
                    <Text fz="sm" fw={500}>
                      {matchedCompanyId}
                    </Text>
                  </Box>
                </Flex>

                {/* Matched Company - Industry Category */}
                {Array.isArray(otherCompany?.industryCategory) && otherCompany.industryCategory.length > 0 && (
                  <Box mb="xs">
                    <Text fz="xs" c="slate.5" mb={4}>
                      Type of Company
                    </Text>
                    <Text fz="sm">
                      {getIndustryLabel(otherCompany.industryCategory[0])}
                      {otherCompany.industryCategory.length > 1 && (
                        <Text span c="slate.5"> +{otherCompany.industryCategory.length - 1}</Text>
                      )}
                    </Text>
                  </Box>
                )}

                {/* Matched Company - Preferred Regions */}
                {otherCompany?.preferredCollaborationRegions &&
                  otherCompany.preferredCollaborationRegions.length > 0 && (
                    <Box>
                      <Text fz="xs" c="slate.5" mb={4}>
                        Preferred Collaboration Regions
                      </Text>
                      <Flex gap="xs" wrap="wrap">
                        {otherCompany.preferredCollaborationRegions.map((region: string, index: number) => (
                          <Badge
                            key={index}
                            variant="light"
                            color="slate"
                            c="black"
                            py={10}
                            px={8}
                            size="md"
                            fw={400}
                            tt="none"
                          >
                            {getRegionLabel(region)}
                          </Badge>
                        ))}
                      </Flex>
                    </Box>
                  )}
              </Box>
            </Flex>
          </Paper>

          {/* Next Steps */}
          <Paper radius="lg" p="lg" bg="#2563EB0D" withBorder style={{ borderColor: "#2563EB33" }}>
            <Text fw={400} mb="md">
              Next Steps
            </Text>
            <Flex direction="column" gap="lg">
              {/* Step 1 */}
              <Flex gap="md" align="flex-start">
                <ThemeIcon size={32} radius={32} color="primary">
                  <Text fz="xs" fw={400} c="white">
                    1
                  </Text>
                </ThemeIcon>
                <Box>
                  <Text fz="sm" fw={400}>
                    Complete Payment
                  </Text>
                  <Text fz="xs" c="slate.5">
                    Pay the collaboration fee to unlock full company details and start the collaboration workspace
                  </Text>
                </Box>
              </Flex>

              {/* Step 2 */}
              <Flex gap="md" align="flex-start">
                <ThemeIcon size={32} radius={32} color="gray" variant="light">
                  <Text fz="xs" fw={400} c="slate.5">
                    2
                  </Text>
                </ThemeIcon>
                <Box>
                  <Text fz="sm" fw={400}>
                    Collaboration Workspace
                  </Text>
                  <Text fz="xs" c="slate.5">
                    After both parties pay (NDA accepted during payment), access your workspace with document sharing
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Paper>
        </Paper>
      </Box>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        opened={paymentModalOpened}
        onClose={closePaymentModal}
        onConfirm={handlePaymentConfirm}
        loading={paymentLoading}
        creditsRequired={engagementFeeCredits}
      />
      <CollaborationPaymentSuccessModal
        opened={!!successPaymentData}
        onClose={() => setSuccessPaymentData(null)}
        paymentData={successPaymentData}
      />
    </Box>
  );
}
